/**
 * 領収書の分割発行における「同一商品のプール化」と「コピーごとの注文ユニット割当」（ODR-1704）。
 *
 * 旧実装は submit で「1枚分の receipt_paper_orders ＋ count」を送り、バックエンドが
 * 同じセットを count 枚ぶん複製していた。そのため全コピーが同じ order_id を参照し、
 * 「別注文（各数量1）だが同じ商品」から複数枚の同一領収書を発行できなかった
 * （例: 飲み放題90分 が別注文で2件あっても1枚しか出せない）。
 *
 * 本モジュールは、同一商品（menu_id＋オプション）を注文をまたいでプールし、
 * 発行枚数ぶんのコピーそれぞれに「実際に使う注文ユニット」を割り当てる。
 * これにより各コピーは別々の order_id を参照でき、過剰消費も起きない。
 *
 * DOM や Vue インスタンスに依存しないため、そのままユニットテストできる。
 */

/** 領収書1枚に載せる注文ユニット1つぶん（receipt_paper_orders の1行に相当） */
export interface OrderUnit {
  order_id: string;
  price: number;
}

/** 選択した商品プール1つぶん */
export interface ReceiptItemSelection {
  /** このプールで使えるユニット（発行済みを除く）。同一商品なら別注文のぶんも含む */
  units: OrderUnit[];
  /** 1枚あたり個数（0以下ならこの領収書に含めない） */
  perReceipt: number;
}

/**
 * 同一商品の判定キー。
 *
 * menu_id が同じでもオプションが違えば別商品として扱う（例: 味付けしお / タレ）。
 * オプションの並び順には依存しない。
 *
 * @param menuId メニューID
 * @param optionNames オプション名の配列
 * @return 同一商品なら一致するキー
 */
export function receiptProductKey(menuId: string, optionNames: string[]): string {
  const options = [...(optionNames || [])].sort();

  return [menuId, ...options].join("|");
}

/**
 * 発行枚数ぶんのコピーそれぞれに、選択した各商品プールから
 * 「1枚あたり個数」ぶんの注文ユニットを割り当てる。
 *
 * プールは先頭から順に消費するので、別注文の同一商品でも自然に配分される。
 * ユニットが足りずコピーを完全に埋められない場合、そのコピー以降は作らない
 * （中身の欠けた領収書を発行しないため）。呼び出し側は maxIssuableCount で
 * 発行枚数を制限しておくこと。
 *
 * @param selections 選択した商品プール
 * @param count 発行枚数
 * @return コピーごとの注文ユニット配列（receipt_paper_orders 相当）
 */
export function allocateReceiptOrders(
  selections: ReceiptItemSelection[],
  count: number
): OrderUnit[][] {
  const active = selections.filter((selection) => selection.perReceipt > 0);
  const cursors = active.map(() => 0);
  const copies: OrderUnit[][] = [];

  for (let i = 0; i < count; i++) {
    const copy: OrderUnit[] = [];
    let fulfilled = true;

    active.forEach((selection, index) => {
      for (let k = 0; k < selection.perReceipt; k++) {
        const unit = selection.units[cursors[index]];
        if (!unit) {
          fulfilled = false;

          return;
        }
        copy.push(unit);
        cursors[index] += 1;
      }
    });

    if (!fulfilled) {
      break;
    }
    copies.push(copy);
  }

  return copies;
}
